/**
 * Ce que le ticket d'une séance dit du temps qui reste.
 *
 * Baptiste, le 2026-11-04 : « le jour J, ce serait bien d'avoir votre
 * rendez-vous dans 1 h 30, dans 1 h […] pour vraiment les inciter à venir ».
 * Avant, le ticket disait « Aujourd'hui » du matin jusqu'à l'heure dite, calculé
 * une fois par le serveur : rien ne bougeait à l'écran le jour où ça compte.
 *
 * Quatre temps :
 *
 * - **avant le jour J** — rien ne change : « Demain », « Dans 4 jours », et la
 *   date en grand ;
 * - **le jour J** — le décompte prend la place de la date (« Dans 3 h 20 ») et la
 *   date passe en surtitre ;
 * - **le dernier quart d'heure** — le surtitre invite à se connecter ;
 * - **pendant la séance** — « En cours », depuis combien de temps : qui arrive en
 *   retard sait qu'il peut encore entrer.
 *
 * ⚠️ **Le jour se compte à l'heure de Paris**, comme partout dans l'Oasis (le
 * serveur met toutes les dates en forme dans ce fuseau). Le décompte en heures,
 * lui, ne dépend d'aucun fuseau : c'est une différence d'instants.
 *
 * ⚠️ **Les minutes s'arrondissent au-dessus** : « Dans 1 min » jusqu'à l'heure
 * dite, jamais « Dans 0 min ».
 *
 * Pur et sans dépendance : le composant `CompteARebours` le rappelle chaque
 * seconde dans le navigateur, et les tests le pilotent avec des instants fixes.
 */

const MINUTE = 60_000

/** À partir de combien de minutes avant le début on invite à se connecter. */
export const MINUTES_IMMINENT = 15

export type PhaseSeance = 'loin' | 'jour-j' | 'imminent' | 'en-cours' | 'terminee'

export type Quand = {
  phase: PhaseSeance
  /** La petite ligne au-dessus. */
  surtitre: string
  /** La ligne en grand : la date, ou le décompte le jour J. */
  grand: string
}

export function quandSeance({
  debut,
  fin,
  maintenant,
  jour,
  surtitre,
}: {
  debut: Date
  fin: Date
  maintenant: Date
  /** « Mercredi 4 novembre » — la date telle que l'écran l'écrit. */
  jour: string
  /** Le surtitre de l'écran avant le jour J, s'il en impose un (l'agenda). */
  surtitre?: string
}): Quand {
  const t = maintenant.getTime()

  if (t >= fin.getTime()) {
    return { phase: 'terminee', surtitre: 'Séance terminée', grand: jour }
  }

  if (t >= debut.getTime()) {
    const depuis = Math.floor((t - debut.getTime()) / MINUTE)
    return {
      phase: 'en-cours',
      surtitre: depuis < 1 ? 'Ça commence' : `Commencée il y a ${duree(depuis)}`,
      grand: 'En cours',
    }
  }

  const ecart = joursEntre(maintenant, debut)
  if (ecart >= 1) {
    const relatif = ecart === 1 ? 'Demain' : `Dans ${ecart} jours`
    return { phase: 'loin', surtitre: surtitre ?? relatif, grand: jour }
  }

  const reste = Math.ceil((debut.getTime() - t) / MINUTE)
  if (reste <= MINUTES_IMMINENT) {
    return { phase: 'imminent', surtitre: 'C’est bientôt, connectez-vous', grand: `Dans ${duree(reste)}` }
  }
  return { phase: 'jour-j', surtitre: `Aujourd’hui · ${minuscule(jour)}`, grand: `Dans ${duree(reste)}` }
}

/** 45 → « 45 min » ; 90 → « 1 h 30 » ; 120 → « 2 h ». */
export function duree(minutes: number): string {
  if (minutes < 60) return `${minutes} min`
  const h = Math.floor(minutes / 60)
  const m = minutes % 60
  return m ? `${h} h ${String(m).padStart(2, '0')}` : `${h} h`
}

/** Jours calendaires entre deux instants, à l'heure de Paris. */
function joursEntre(a: Date, b: Date): number {
  return Math.round((minuitParis(b) - minuitParis(a)) / 86_400_000)
}

const JOUR_PARIS = new Intl.DateTimeFormat('en-CA', {
  timeZone: 'Europe/Paris',
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
})

function minuitParis(d: Date): number {
  return Date.parse(`${JOUR_PARIS.format(d)}T00:00:00Z`)
}

function minuscule(texte: string): string {
  return texte.charAt(0).toLowerCase() + texte.slice(1)
}
